import { useState } from "react";
import {
  Box,
  Button,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { CloseButton, Modal } from "@rentablo/components";

const TOTAL_STEPS = 9;

const stepImageModules = import.meta.glob<{ default: string }>(
  "./images/step-*.png",
  { eager: true }
);

const stepImages = Object.fromEntries(
  Object.entries(stepImageModules).map(([path, mod]) => {
    const match = path.match(/step-(\d+)\.png$/);
    return [match ? Number(match[1]) : -1, mod.default];
  })
);

const HeaderRow = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 2,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(4, 5, 4, 5),
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(3),
}));

const Body = styled(Box)(({ theme }) => ({
  padding: `0 ${theme.spacing(5)}`,
}));

const HeaderCenter = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));

const ScreenshotWrap = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.border.primary}`,
  borderRadius: 8,
  marginTop: theme.spacing(4),
}));

const Screenshot = styled("img")({
  width: "100%",
  borderRadius: 6,
  display: "block",
});

const FooterRow = styled(Box)(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  zIndex: 2,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(4, 5, 4, 5),
  marginTop: theme.spacing(5),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing(4),
  flexWrap: "wrap",
}));

const Dots = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5),
}));

const OrderedList = styled("ol")(({ theme }) => ({
  margin: 0,
  paddingLeft: 0,
  listStyle: "none",
  counterReset: "kics-step",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const OrderedItem = styled("li")(({ theme }) => ({
  counterIncrement: "kics-step",
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(3),
  "&::before": {
    content: "counter(kics-step)",
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: 13,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
}));

const ORDERED_LINE_RE = /^\(\d+\)\s+/;

const StepDescription = ({ i18nKey }: { i18nKey: string }) => {
  const { t } = useTranslation();
  const description = t(i18nKey);
  const lines = description.split("\n").filter((l) => l.length > 0);
  const isOrdered =
    lines.length > 1 && lines.every((l) => ORDERED_LINE_RE.test(l));

  if (isOrdered) {
    return (
      <OrderedList>
        {lines.map((line, idx) => (
          <OrderedItem key={idx}>
            <Typography variant="body3" color="textSecondary" mt="3px">
              {line.replace(ORDERED_LINE_RE, "")}
            </Typography>
          </OrderedItem>
        ))}
      </OrderedList>
    );
  }

  return (
    <Typography
      variant="body3"
      color="textSecondary"
      component="div"
      sx={{
        whiteSpace: "pre-line",
        ul: {
          margin: 0,
        },
      }}
    >
      <Trans
        i18nKey={i18nKey}
        values={{
          complianceUrl: import.meta.env.VITE_APP_ROOT_URL,
        }}
        components={{
          br: <br />,
          ul: <ul />,
          ol: <ol />,
          li: <li />,
          u: <u />,
        }}
      />
    </Typography>
  );
};

const Dot = styled(Box)<{ active: boolean }>(({ theme, active }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.border.primary,
  transition: "background-color 0.15s ease",
}));

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const KicsOnboardingModal = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLessSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [step, setStep] = useState(1);

  const handleClose = () => {
    onClose();
    setStep(1);
  };

  const isFirst = step === 1;
  const isLast = step === TOTAL_STEPS;
  const stepImage = stepImages[step];
  const stepTitle = t(`kpmg.kicsOnboarding.step${step}.title`);
  const stepDescriptionKey = `kpmg.kicsOnboarding.step${step}.description`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onCloseButtonClick={handleClose}
      borderRadius="12px"
      width={780}
      maxHeight="90vh"
      contentPadding="0"
      disableScrollLock
      testId="kics-onboarding-modal"
    >
      <HeaderRow>
        <HeaderCenter>
          <Typography variant="body4" color="text.tertiary">
            {t("kpmg.kicsOnboarding.stepCounter", {
              step,
              total: TOTAL_STEPS,
            })}
          </Typography>
          <Typography variant="h6" textAlign="left">
            {stepTitle}
          </Typography>
        </HeaderCenter>
        <CloseButton onClose={handleClose} />
      </HeaderRow>

      <Body>
        <StepDescription i18nKey={stepDescriptionKey} />

        {stepImage && (
          <ScreenshotWrap>
            <Screenshot src={stepImage} alt={stepTitle} />
          </ScreenshotWrap>
        )}
      </Body>

      <FooterRow>
        <Dots role="list" aria-label="Progress">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(
            (dotStep) => (
              <Dot key={dotStep} role="listitem" active={dotStep === step} />
            )
          )}
        </Dots>
        <Box
          display="flex"
          gap={3}
          flexDirection={isLessSm ? "column-reverse" : "row"}
          width={isLessSm ? "100%" : "auto"}
        >
          <Button
            variant="tertiary"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={isFirst}
            data-testid="kics-onboarding-back-btn"
          >
            {t("kpmg.kicsOnboarding.buttons.back")}
          </Button>
          <Button
            variant="primary"
            onClick={
              isLast
                ? handleClose
                : () => setStep((s) => Math.min(TOTAL_STEPS, s + 1))
            }
            data-testid="kics-onboarding-next"
          >
            {isLast
              ? t("kpmg.kicsOnboarding.buttons.done")
              : t("kpmg.kicsOnboarding.buttons.next")}
          </Button>
        </Box>
      </FooterRow>
    </Modal>
  );
};
